import { useCallback, useEffect, useState } from "react";
import {
  CampaignServiceError,
  createOpportunity,
  createProgram,
  getCampaignHierarchy,
  updateCampaign as updateCampaignApi,
  updateOpportunity as updateOpportunityApi,
} from "../api";
import {
  CampaignHierarchyItem,
  CreateOpportunityRequest,
  CreateProgramRequest,
  OpportunityHierarchyItem,
  ProgramHierarchyItem,
} from "../types";
import { CAMPAIGN_STATUSES } from "../types/campaign-status.constants";

interface UseCampaignBuilderResult {
  campaign: CampaignHierarchyItem | null;
  programs: ProgramHierarchyItem[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  addProgram: (payload: Omit<CreateProgramRequest, "campaignId">) => Promise<ProgramHierarchyItem>;
  addOpportunity: (
    payload: Omit<CreateOpportunityRequest, "programId"> & {
      programId: string;
    },
  ) => Promise<OpportunityHierarchyItem>;
  updateCampaign: (
    payload: Partial<CreateProgramRequest> & {
      name?: string;
      description?: string;
      status?: CAMPAIGN_STATUSES;
      startDate?: string;
      endDate?: string;
    },
  ) => Promise<void>;
  updateOpportunity: (
    payload: Partial<
      Omit<CreateOpportunityRequest, "programId"> & {
        id: string;
        programId: string;
      }
    >,
  ) => Promise<void>;
  retry: () => Promise<void>;
}

export function useCampaignBuilder(campaignId: string): UseCampaignBuilderResult {
  const [campaign, setCampaign] = useState<CampaignHierarchyItem | null>(null);
  const [programs, setPrograms] = useState<ProgramHierarchyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCampaignPrograms = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const hierarchy = await getCampaignHierarchy();
      const currentCampaign = hierarchy.campaigns.find((campaign) => campaign.id === campaignId);
      setCampaign(currentCampaign || null);
      setPrograms(currentCampaign?.programs ?? []);
    } catch (loadError) {
      setCampaign(null);
      setPrograms([]);
      setError(
        loadError instanceof CampaignServiceError || loadError instanceof Error
          ? loadError.message
          : "Failed to load campaign programs.",
      );
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    void loadCampaignPrograms();
  }, [loadCampaignPrograms]);

  const addProgram = async (payload: Omit<CreateProgramRequest, "campaignId">) => {
    setSaving(true);
    setError(null);

    try {
      const createdProgram = await createProgram(campaignId, payload);

      const nextProgram: ProgramHierarchyItem = {
        id: createdProgram.id,
        campaignId: createdProgram.campaignId,
        name: createdProgram.name,
        description: payload.description,
        status: payload.status as CAMPAIGN_STATUSES,
        opportunityCount: 0,
        opportunities: [],
      };

      setPrograms((current) => [...current, nextProgram]);
      return nextProgram;
    } catch (saveError) {
      setError(
        saveError instanceof CampaignServiceError || saveError instanceof Error
          ? saveError.message
          : "Failed to create program.",
      );
      throw saveError;
    } finally {
      setSaving(false);
    }
  };

  const addOpportunity = async (
    payload: Omit<CreateOpportunityRequest, "programId"> & {
      programId: string;
    },
  ) => {
    setSaving(true);
    setError(null);

    try {
      const createdOpportunity = await createOpportunity(payload.programId, {
        type: payload.type,
      });

      const nextOpportunity: OpportunityHierarchyItem = {
        id: createdOpportunity.id,
        programId: createdOpportunity.programId,
        name: createdOpportunity.name,
        type: payload.type,
        neighborhoodId: createdOpportunity.neighborhoodId,
        neighborhoodName: createdOpportunity.neighborhoodName,
      };

      setPrograms((current) =>
        current.map((program) =>
          program.id === payload.programId
            ? {
                ...program,
                opportunityCount: program.opportunityCount + 1,
                opportunities: [...program.opportunities, nextOpportunity],
              }
            : program,
        ),
      );
      return nextOpportunity;
    } catch (saveError) {
      setError(
        saveError instanceof CampaignServiceError || saveError instanceof Error
          ? saveError.message
          : "Failed to create opportunity.",
      );
      throw saveError;
    } finally {
      setSaving(false);
    }
  };

  const updateCampaign = async (payload: {
    name?: string;
    description?: string;
    status?: CAMPAIGN_STATUSES;
    startDate?: string;
    endDate?: string;
  }) => {
    setSaving(true);
    setError(null);

    try {
      await updateCampaignApi(campaignId, payload);
      setCampaign((current) =>
        current
          ? {
              ...current,
              name: payload.name ?? current.name,
              description:
                payload.description !== undefined ? payload.description : current.description,
              status: payload.status ?? current.status,
              startDate: payload.startDate !== undefined ? payload.startDate : current.startDate,
              endDate: payload.endDate !== undefined ? payload.endDate : current.endDate,
            }
          : current,
      );
    } catch (saveError) {
      setError(
        saveError instanceof CampaignServiceError || saveError instanceof Error
          ? saveError.message
          : "Failed to update campaign.",
      );
      throw saveError;
    } finally {
      setSaving(false);
    }
  };

  const updateOpportunity = async (
    payload: Partial<
      Omit<CreateOpportunityRequest, "programId"> & {
        id: string;
        programId: string;
      }
    >,
  ) => {
    if (!payload.id || !payload.programId) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      setPrograms((current) =>
        current.map((program) =>
          program.id !== payload.programId
            ? program
            : {
                ...program,
                opportunities: program.opportunities.map((opportunity) =>
                  opportunity.id !== payload.id
                    ? opportunity
                    : {
                        ...opportunity,
                        type: opportunity.type,
                        startDate: opportunity.startDate,
                        endDate: opportunity.endDate,
                      },
                ),
              },
        ),
      );
    } catch (saveError) {
      setError(
        saveError instanceof CampaignServiceError || saveError instanceof Error
          ? saveError.message
          : "Failed to update opportunity.",
      );
      throw saveError;
    } finally {
      setSaving(false);
    }
  };

  return {
    campaign,
    programs,
    loading,
    saving,
    error,
    addProgram,
    addOpportunity,
    updateCampaign,
    updateOpportunity,
    retry: loadCampaignPrograms,
  };
}
